import type { ICalendarAvailability, ICalendarAvailabilityV2 } from "@app/availability/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@app/componentLibrary/ui/tooltip/Tooltip";
import classNames from "classnames";
import {
    addDays,
    differenceInDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    startOfMonth,
    startOfWeek,
    subDays,
} from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import ConditionalWrapper from "../ConditionalWrapper";
import { PriceableItemPrice } from "../PriceableItemPrice";
import { SelectableDateTypes } from "./constants/SelectableDateTypes";

export enum SelectableDates {
    Future = "future",
    Past = "past",
    Both = "both",
}

interface ICalendarSelectionProps {
    currentDate: Date;
    startSelectedDate?: Date;
    endSelectedDate?: Date;
    onChange: any;
    isFutureDateDisabled?: boolean;
    isRangeSelection?: boolean;
    selectableDates: SelectableDateTypes;
    availability?: ICalendarAvailability;
}

const DATE_KEY_FORMAT = "yyyy-MM-dd";

interface DayInfo {
    date: Date;
    isCurrentMonth: boolean;
    isSelected: boolean;
    isStartSelected: boolean;
    isEndSelected: boolean;
    isBetweenSelectedDates: boolean;
    isMinLosDisabled: boolean;
    isToday: boolean;
    isFuture: boolean;
    isPast: boolean;
    isSelectable: boolean;
    isAvailable: boolean;
    isDisabled: boolean;
    isStrikeThrough: boolean;
    showRates: boolean;
    min_rate: number | null;
    min_los: number | undefined;
    runningMinLos: number;
    selectedDateDistance: number | undefined;
    isFirstDisabledDayAvailableForCheckout: boolean;
    isAfterFirstDisabledDayAvailableForCheckout: boolean;
}

const isV2Availability = (availability: ICalendarAvailability[number]): availability is ICalendarAvailabilityV2[number] =>
    typeof availability === "object";

const getV2Value = (availability: ICalendarAvailability[number]) => (isV2Availability(availability) ? availability.min_rate : availability);

const getV2Mlos = (availability: ICalendarAvailability[number]) => (isV2Availability(availability) ? availability.min_los : 0);

export const CalendarSelection = ({
    currentDate,
    startSelectedDate,
    endSelectedDate,
    onChange,
    isRangeSelection = false,
    selectableDates,
    availability,
}: ICalendarSelectionProps) => {
    const { t } = useTranslation("availability");

    const today = useMemo(() => new Date(), []);
    const todayEndBookableTime = useMemo(() => endOfDay(today), [today]); // until property config we will use the end of the day

    /** Helper to check if every availability entry is a numeric rate */
    const hasRates = useMemo(
        () =>
            Object.values(availability || {}).every((entry) =>
                isV2Availability(entry) ? typeof entry.min_rate === "number" : typeof entry === "number"
            ),
        [availability]
    );

    /** Generate the grid of days */
    const days: DayInfo[] = useMemo(() => {
        const firstDateOnCalendarMonth = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
        const startSelectedDateOnPreviousMonth = startSelectedDate ? isBefore(startSelectedDate, firstDateOnCalendarMonth) : false;
        const start = startSelectedDate && startSelectedDateOnPreviousMonth ? startSelectedDate : firstDateOnCalendarMonth;
        const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
        const result: DayInfo[] = [];

        // current date being processed
        let pointer = start;
        // higest valid mlos since startSelectedDate
        let runningMinLos = 0;
        // distance between startSelectedDate and current pointer
        let selectedDateDistance = startSelectedDate ? differenceInDays(pointer, startSelectedDate) : undefined;
        // first disabled day available for checkout
        let firstDisabledDayAvailableForCheckout: Date | null = null;

        while (pointer <= end) {
            const dateKey = format(pointer, DATE_KEY_FORMAT);

            const rate = getV2Value(availability?.[dateKey] || false);
            const min_los = getV2Mlos(availability?.[dateKey] || false);

            // update running mlos if current mlos is higher than the previous one
            if (startSelectedDate && isAfter(pointer, subDays(startSelectedDate, 1)) && min_los > runningMinLos) {
                runningMinLos = min_los || 0;
            }

            const isFuture = isAfter(pointer, today);
            const isPast = isBefore(pointer, subDays(today, 1));
            const isToday = isSameDay(pointer, today);
            const isCurrentMonth = isSameMonth(pointer, currentDate);

            const isAvailable = !!rate;

            const isStartSelected = !!startSelectedDate && isSameDay(pointer, startSelectedDate);
            const isEndSelected = !!endSelectedDate && isSameDay(pointer, endSelectedDate);
            const isBetweenSelectedDates = !!(
                startSelectedDate &&
                endSelectedDate &&
                isBefore(startSelectedDate, pointer) &&
                isAfter(endSelectedDate, pointer)
            );
            const isSelected = isStartSelected || isEndSelected || isBetweenSelectedDates;

            let isFirstDisabledDayAvailableForCheckout = false;
            const isAfterFirstDisabledDayAvailableForCheckout = !!firstDisabledDayAvailableForCheckout;
            // if startSelectedDate is set and endSelectedDate is not set, and we haven't found a first disabled day yet, and the current day has no rate, then this is the first disabled day available for checkout
            if (
                startSelectedDate &&
                !endSelectedDate &&
                isCurrentMonth &&
                firstDisabledDayAvailableForCheckout === null &&
                !rate &&
                isAfter(pointer, startSelectedDate)
            ) {
                firstDisabledDayAvailableForCheckout = pointer;
                isFirstDisabledDayAvailableForCheckout = true;
            }

            const noRate = !rate;
            const isMinLosDisabled =
                (selectedDateDistance && selectedDateDistance > 0 ? selectedDateDistance < runningMinLos : false) && !isSelected;
            const isAfterCutoff =
                !isBefore(today, todayEndBookableTime) &&
                (selectableDates === SelectableDateTypes.Future || selectableDates === SelectableDateTypes.Both);
            const isDisabled =
                (noRate && !isFirstDisabledDayAvailableForCheckout) ||
                (selectableDates === SelectableDateTypes.Past && isFuture) ||
                (selectableDates === SelectableDateTypes.Future && isPast) ||
                isAfterCutoff ||
                isMinLosDisabled;

            const showRates = endSelectedDate
                ? hasRates && !!rate && !isSameDay(pointer, endSelectedDate)
                : hasRates && (!isMinLosDisabled || isSelected);

            const isSelectable = !!rate && !isMinLosDisabled;

            const isStrikeThrough = isDisabled && !isSelected;

            result.push({
                // Date related info
                date: pointer,
                isFuture,
                isPast,
                isToday,
                isCurrentMonth,
                // Selection related info
                isSelected,
                isStartSelected,
                isEndSelected,
                isBetweenSelectedDates,
                // Availability related info
                isMinLosDisabled,
                isSelectable,
                isAvailable,
                isDisabled,
                isStrikeThrough,
                showRates,
                min_rate: Number(rate),
                min_los,
                runningMinLos,
                selectedDateDistance,
                isFirstDisabledDayAvailableForCheckout,
                isAfterFirstDisabledDayAvailableForCheckout,
            });

            pointer = addDays(pointer, 1);
            // update the selected date distance if startSelectedDate is set
            if (selectedDateDistance !== undefined) {
                selectedDateDistance++;
            }
        }

        // if the start date is set and it's in the previous month, we need to filter out the days before the start date
        if (startSelectedDateOnPreviousMonth) {
            return result.filter((day) => isAfter(day.date, subDays(firstDateOnCalendarMonth, 1)));
        }

        return result;
    }, [currentDate, startSelectedDate, availability, today, endSelectedDate, todayEndBookableTime, selectableDates, hasRates]);

    /** Handle date click */
    const handleSelectDate = useCallback(
        (date: Date) => {
            // when a selection is already made. we restart the selection form the new date
            if (startSelectedDate && endSelectedDate) {
                onChange({ startSelectedDate: date, endSelectedDate: null });
                return;
            }

            if (isRangeSelection && startSelectedDate && !endSelectedDate && isAfter(date, startSelectedDate)) {
                // Validate that every date in the range is selectable
                const betweenDates = days.filter(
                    (day) => isAfter(day.date, subDays(startSelectedDate, 1)) && isBefore(day.date, addDays(date, 1))
                );

                const rangeIsAvailable = betweenDates.every(
                    (d) => (d.isAvailable || d.isFirstDisabledDayAvailableForCheckout) && !d.isAfterFirstDisabledDayAvailableForCheckout
                );

                if (rangeIsAvailable) {
                    onChange({ startSelectedDate, endSelectedDate: date });
                } else {
                    onChange({ startSelectedDate: date, endSelectedDate: null });
                }
            } else {
                onChange({ startSelectedDate: date, endSelectedDate: null });
            }
        },
        [startSelectedDate, endSelectedDate, isRangeSelection, onChange, days]
    );

    return (
        <div className="w-full pb-3">
            <div className="grid grid-cols-7 text-xs leading-6 text-gray-300 my-2">
                <div>M</div>
                <div>T</div>
                <div>W</div>
                <div>T</div>
                <div>F</div>
                <div>S</div>
                <div>S</div>
            </div>
            <div className="grid grid-cols-7 rounded-lg text-xs gap-y-3">
                <TooltipProvider delayDuration={0}>
                    {days.map((day, dayIdx) =>
                        !isSameMonth(day.date, currentDate) ? (
                            <div key={dayIdx} />
                        ) : (
                            <ConditionalWrapper
                                key={format(day.date, "dd-MM-yyyy")}
                                condition={day.isMinLosDisabled}
                                wrapper={(children) => (
                                    <Tooltip>
                                        <TooltipTrigger asChild>{children}</TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t("search.minStay", { night: day.runningMinLos })}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            >
                                <button
                                    data-testid={`calendar-day-${format(day.date, "dd-MM-yyyy")}`}
                                    type="button"
                                    onClick={() => handleSelectDate(day.date)}
                                    disabled={day.isDisabled}
                                    className={twMerge(
                                        "focus:z-1 font-medium group",
                                        classNames({
                                            "hover:rounded-lg hover:bg-primary hover:text-white":
                                                day.isCurrentMonth &&
                                                (day.isSelectable || day.isFirstDisabledDayAvailableForCheckout) &&
                                                !day.isToday &&
                                                !day.isSelected,
                                            "bg-transparant text-gray-900":
                                                !day.isSelected && day.isCurrentMonth && !day.isToday && day.isSelectable,
                                            "text-gray-300 cursor-not-allowed": day.isDisabled,
                                            "bg-secondary text-primary": day.isSelected && !!endSelectedDate,
                                        })
                                    )}
                                >
                                    <time
                                        dateTime={format(day.date, "dd-MM-yyyy")}
                                        className={classNames("flex h-full w-full items-center justify-center", {
                                            "rounded-lg bg-primary text-primary-foreground": day.isStartSelected || day.isEndSelected,
                                            "rounded-lg bg-gray-100 hover:bg-primary text-gray-800": day.isToday,
                                        })}
                                    >
                                        <div
                                            className={classNames("h-11 w-14 justify-between flex flex-col items-center relative py-2 ", {
                                                "justify-between": hasRates,
                                                "justify-center": !hasRates,
                                                "group-hover:text-white": day.isSelectable && !day.isSelected,
                                                "h-14": hasRates,
                                                "text-gray-300":
                                                    (selectableDates === SelectableDateTypes.Past && isAfter(day.date, new Date())) ||
                                                    (selectableDates === SelectableDateTypes.Future &&
                                                        isBefore(day.date, new Date()) &&
                                                        day.isSelected),
                                            })}
                                        >
                                            <div className="flex items-center justify-center">
                                                {day.isStrikeThrough ? (
                                                    <div className="border-t border-b border-gray-300 border-b-white w-4 h-0 absolute" />
                                                ) : null}

                                                <span className={twMerge("z-10", classNames({ "font-medium text-sm": hasRates }))}>
                                                    {format(day.date, "d")}
                                                </span>
                                            </div>

                                            {day.showRates ? (
                                                <PriceableItemPrice
                                                    item={day.min_rate || 0}
                                                    className="justify-center"
                                                    compact
                                                    moneyClassName={twMerge(
                                                        "whitespace-nowrap text-xs font-normal text-[#A3A3A3]",
                                                        classNames({
                                                            "text-color-secondary":
                                                                day.isSelected || day.isStartSelected || day.isEndSelected,
                                                            "group-hover:text-white": !day.isSelected,
                                                        })
                                                    )}
                                                />
                                            ) : null}
                                        </div>
                                    </time>
                                </button>
                            </ConditionalWrapper>
                        )
                    )}
                </TooltipProvider>
            </div>
        </div>
    );
};
